// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Retenues » (Paie en MODE VÉRIFICATION) : par période, lot de l'organisation,
// retenues proposées (état, verdict de l'arbitre, motif de blocage : FONDEMENT_ABSENT, TRANSMISSION_DESACTIVEE…),
// régularisations, et simulation de l'arbitre (« ce qui serait retenu »). Aucune action de transmission : rien
// n'est transmis à la Paie ni prélevé sur un salaire.
// Arrêt du lot de la période en deux temps (ArretLot.jsx, étape 5), sans aucune transmission.
// Vue « Fondements de retenue » : enregistrement, confirmation par une autre personne, consultation (FondementsRetenue.jsx).
// Routes : GET /api/dispositifs/paie/periodes/:periode?organisation=, GET /evenements/:id, POST /simulations.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { ChevronLeft, ChevronRight, FlaskConical, ListChecks } from 'lucide-react';
import { Badge, Bouton, BandeauInfo, Squelette } from '../../design/composants';
import { toArray, libelle } from './api';
import { Etat, PaginationDs, Fenetre, ErreurServeur } from './commun';
import {
  useSocial, francs, moisLong, periodeCourante, periodeDecalee, dateHeureLongue, STATUTS_RETENUE, VERDICTS, STATUTS_LOT, NATURES_RETENUE, ETATS_SIMULES, MOTIFS_BLOCAGE,
} from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, BandeauRetenuesSimulation, Tuiles, Montant, FenetreActe } from './communSocial';
import FondementsRetenue from './FondementsRetenue';
import ArretLot from './ArretLot';

const FILTRES = ['PROPOSE', 'VALIDE', 'ANNULE'];

const motifsDe = (e) => toArray(e.motifs_blocage).map((m) => ({ code: m, texte: MOTIFS_BLOCAGE[m] || (toArray(e.motifs_libelles)[toArray(e.motifs_blocage).indexOf(m)]) || m }));

function Motifs({ e }) {
  const l = motifsDe(e);
  if (!l.length) return <span className="rh-cellule-secondaire">Aucun</span>;
  return <ul style={{ margin: 0, paddingLeft: 16 }}>{l.map((m) => <li key={m.code} title={m.code}>{m.texte}</li>)}</ul>;
}

function FenetreEvenement({ id, onFermer }) {
  const api = useSocial();
  const d = useLectureSociale(() => api.retenue(id), [api, id]);
  const e = d.donnees;
  return (
    <Fenetre titre="Retenue proposée" Icone={ListChecks} onFermer={onFermer} large>
      {d.erreur && <ErreurServeur erreur={d.erreur} />}
      {!e && !d.erreur && <Squelette largeur="60%" />}
      {e && (
        <>
          <p style={{ margin: '0 0 8px', fontSize: 14 }}>
            <strong>{e.salarie?.nom}</strong> ({e.salarie?.matricule}) — {e.type_libelle}, {libelle(NATURES_RETENUE, e.nature).toLowerCase()}{e.echeance ? `, échéance ${e.echeance}` : ''} — {francs(e.montant_demande)} en {moisLong(e.periode)}.
          </p>
          <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>{e.message}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}><Etat table={STATUTS_RETENUE} code={e.statut} />{e.verdict && <Etat table={VERDICTS} code={e.verdict} />}</div>
          <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Motifs de blocage</h3>
          <Motifs e={e} />
          {toArray(e.avertissements_libelles).length > 0 && <><h3 style={{ fontSize: 14, margin: '12px 0 6px' }}>Avertissements</h3><ul style={{ margin: 0, paddingLeft: 16 }}>{toArray(e.avertissements_libelles).map((a) => <li key={a}>{a}</li>)}</ul></>}
          <h3 style={{ fontSize: 14, margin: '12px 0 6px' }}>Historique</h3>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Date</th><th>État</th><th>Verdict</th><th>Par</th><th>Motif</th></tr></thead>
              <tbody>
                {toArray(e.historique).map((h, i) => (
                  <tr key={`${h.le}-${i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dateHeureLongue(h.le)}</td>
                    <td>{h.de_statut ? `${libelle(STATUTS_RETENUE, h.de_statut)} → ` : ''}{libelle(STATUTS_RETENUE, h.vers_statut)}</td>
                    <td>{h.verdict ? libelle(VERDICTS, h.verdict) : ''}{toArray(h.motifs_libelles).length ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{toArray(h.motifs_libelles).join(' ; ')}</span> : null}</td>
                    <td>{h.acteur || <span className="rh-cellule-secondaire">Système</span>}</td>
                    <td>{h.motif || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Fenetre>
  );
}

/** Résultat d'une simulation de l'arbitre : synthèse et lignes « ce qui serait retenu ». */
function ResultatSimulation({ r }) {
  const s = r.synthese || {};
  const lignes = toArray(r.lignes);
  const motifs = Object.entries(s.bloques_par_motif || {});
  return (
    <>
      <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>{s.message} Simulation du {dateHeureLongue(r.simulation?.lance_le)}.</p>
      <Tuiles tuiles={[
        { libelle: 'Demandé', valeur: francs(s.total_demande), aide: `${s.nombre_evenements || 0} retenue(s), ${s.nombre_salaries || 0} salarié(s)` },
        { libelle: 'Serait retenu', valeur: francs(s.total_serait_retenu) },
        { libelle: 'Reliquat', valeur: francs(s.total_reliquat), aide: `${s.partiels || 0} partielle(s), ${s.non_consommes || 0} sans retenue` },
        { libelle: 'Exclues', valeur: String(s.exclus || 0) },
      ]} />
      {motifs.length > 0 && (
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          Levés pour la simulation ou bloquants : {motifs.map(([m, n]) => `${MOTIFS_BLOCAGE[m] || m} (${n})`).join(' ; ')}.
        </p>
      )}
      {!lignes.length ? <p className="rh-cellule-secondaire">Aucune retenue à simuler sur cette période.</p> : (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Retenue</th><th style={{ textAlign: 'right' }}>Demandé</th><th style={{ textAlign: 'right' }}>Serait retenu</th><th style={{ textAlign: 'right' }}>Reliquat</th><th>Résultat</th></tr></thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.evenement_id}>
                  <td>{l.libelle_bulletin || l.reference}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.reference}{l.echeance ? ` · ${l.echeance}` : ''} · rang {l.rang_passage}</span></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.montant_demande} /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.montant_serait_retenu} fort /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.reliquat} /></td>
                  <td>
                    <Etat table={ETATS_SIMULES} code={l.statut_simule} />
                    {toArray(l.motifs_libelles).length > 0 && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{toArray(l.motifs_libelles).join(' ; ')}</span>}
                    {l.report_simule && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>Reliquat reporté en {moisLong(l.report_simule.periode)} (simulé)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function FenetreSimulation({ organisationId, periode, onFermer }) {
  const api = useSocial();
  const [resultat, setResultat] = useState(null);
  if (resultat) {
    return <Fenetre titre={`Simulation de ${moisLong(periode)}`} Icone={FlaskConical} onFermer={onFermer} large><ResultatSimulation r={resultat} /></Fenetre>;
  }
  return (
    <FenetreActe titre={`Simuler les retenues de ${moisLong(periode)}`} Icone={FlaskConical} libelleBouton="Lancer la simulation" onFermer={onFermer}
      aide="L'arbitre calcule ce qui serait retenu si le prélèvement était activé : il lève seulement les motifs « en attente du GO » (transmission, fondement, rubrique). Aucune retenue n'est modifiée ; le passage est enregistré."
      onConfirmer={async (cle) => setResultat(await api.simulerRetenues({ periode, organisation_id: organisationId }, cle))} />
  );
}

function FenetreSimulationEnregistree({ id, onFermer }) {
  const api = useSocial();
  const s = useLectureSociale(() => api.simulationRetenues(id), [api, id]);
  return (
    <Fenetre titre="Simulation enregistrée" Icone={FlaskConical} onFermer={onFermer} large>
      {s.erreur && <ErreurServeur erreur={s.erreur} />}
      {!s.donnees && !s.erreur && <Squelette largeur="60%" />}
      {s.donnees && <ResultatSimulation r={s.donnees} />}
    </Fenetre>
  );
}

function RetenuesProposees({ organisation, droits }) {
  const api = useSocial();
  const [periode, setPeriode] = useState(periodeCourante());
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const lecture = useLectureSociale(() => api.retenues(periode, organisation.id, { page, taille: 25, statut }), [api, periode, organisation.id, page, statut]);
  const sims = useLectureSociale(() => api.simulationsRetenues(periode, organisation.id), [api, periode, organisation.id]);
  const d = lecture.donnees;
  const evenements = toArray(d?.evenements);
  const lots = toArray(d?.lots);
  const changerPeriode = (n) => { setPeriode((p) => periodeDecalee(p, n)); setPage(1); setInfo(''); };
  return (
    <>
      <BandeauRetenuesSimulation />
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Bouton petit variante="discret" Icone={ChevronLeft} aria-label="Mois précédent" onClick={() => changerPeriode(-1)} />
          <strong style={{ minWidth: 130, textAlign: 'center', fontSize: 15 }}>{moisLong(periode)}</strong>
          <Bouton petit variante="discret" Icone={ChevronRight} aria-label="Mois suivant" onClick={() => changerPeriode(1)} />
        </div>
        <Bouton Icone={FlaskConical} onClick={() => setFenetre({ type: 'simuler' })}>Simuler ce qui serait retenu</Bouton>
      </div>
      {d && (
        <Tuiles tuiles={[
          { libelle: 'Retenues de la période', valeur: String(d.synthese?.nombre ?? 0) },
          { libelle: 'Total demandé', valeur: francs(d.synthese?.total_demande) },
          { libelle: 'Bloquées', valeur: String(d.synthese?.bloques ?? 0), aide: 'à l\'état « proposée »' },
          { libelle: 'Lot', valeur: lots.length ? libelle(STATUTS_LOT, lots[0].statut) : 'Aucun', aide: lots.length ? 'mode vérification' : 'créé à la première retenue' },
        ]} />
      )}
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      {d && <ArretLot organisation={organisation} periode={periode} lot={lots.find((l) => l.organisation_id === organisation.id) || lots[0] || null} nombre={d.synthese?.nombre || 0} droits={droits}
        onFait={(m) => { setInfo(m); lecture.recharger(); }} />}
      <div className="rh-outils"><FiltreEtats table={STATUTS_RETENUE} codes={FILTRES} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} /></div>
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={{ ...lecture, donnees: d ? d.evenements : null }} Icone={ListChecks} estVide={!evenements.length}
          titreVide={`Aucune retenue en ${moisLong(periode)}`}
          vide="Les retenues sont proposées au décaissement d'un prêt remboursé par retenue (ou à la remise d'une campagne). Elles restent bloquées tant que le fondement juridique manque et que la transmission n'est pas ouverte.">
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Salarié</th><th>Retenue</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Motif du blocage</th></tr></thead>
              <tbody>
                {evenements.map((e) => (
                  <tr key={e.id} className="rh-ligne" tabIndex={0} onClick={() => setFenetre({ type: 'evenement', id: e.id })} onKeyDown={(k) => { if (k.key === 'Enter') setFenetre({ type: 'evenement', id: e.id }); }}>
                    <td><span className="rh-cellule-principale">{e.salarie?.nom}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{e.salarie?.matricule}</span></td>
                    <td>
                      {e.type_libelle}
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>
                        {libelle(NATURES_RETENUE, e.nature)}{e.echeance ? ` · échéance ${e.echeance}` : ''}{e.version_echeancier ? ` · version ${e.version_echeancier}` : ''}
                      </span>
                      {e.nature === 'REGULARISATION' && <Badge ton="info">{Number(e.sens) === -1 ? 'Régularisation en faveur du salarié' : 'Régularisation'}</Badge>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{Number(e.sens) === -1 ? '− ' : ''}{francs(e.montant_demande)}</td>
                    <td><Etat table={STATUTS_RETENUE} code={e.statut} />{e.verdict && <div style={{ marginTop: 4 }}><Etat table={VERDICTS} code={e.verdict} /></div>}</td>
                    <td><Motifs e={e} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={d?.evenements} onPage={setPage} />
        </EtatsLecture>
      </section>
      {toArray(sims.donnees).length > 0 && (
        <section className="rh-carte">
          <h2 className="rh-form-section-titre" style={{ marginTop: 0 }}>Simulations enregistrées</h2>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {toArray(sims.donnees).map((s) => (
              <li key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--sg-bord)', fontSize: 14 }}>
                <span>Simulation du {dateHeureLongue(s.lance_le)}</span>
                <Bouton petit variante="lien" onClick={() => setFenetre({ type: 'simulation', id: s.id })}>Ouvrir</Bouton>
              </li>
            ))}
          </ul>
        </section>
      )}
      {fenetre?.type === 'evenement' && <FenetreEvenement id={fenetre.id} onFermer={() => setFenetre(null)} />}
      {fenetre?.type === 'simuler' && <FenetreSimulation organisationId={organisation.id} periode={periode} onFermer={() => { setFenetre(null); sims.recharger(); }} />}
      {fenetre?.type === 'simulation' && <FenetreSimulationEnregistree id={fenetre.id} onFermer={() => setFenetre(null)} />}
    </>
  );
}

const VUES = [['retenues', 'Retenues proposées'], ['fondements', 'Fondements de retenue']];

export default function RetenuesSection({ organisation, droits }) {
  const [vue, setVue] = useState('retenues');
  return (
    <>
      <div className="rh-vues rh-vues-ligne" role="group" aria-label="Vue des retenues">
        {VUES.map(([c, l]) => <button key={c} type="button" className="rh-vue" aria-pressed={vue === c} onClick={() => setVue(c)}>{l}</button>)}
      </div>
      {vue === 'retenues' && <RetenuesProposees organisation={organisation} droits={droits} />}
      {vue === 'fondements' && <FondementsRetenue organisation={organisation} droits={droits} />}
    </>
  );
}
